import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useActivePage, useNotifications } from '@toolpad/core';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, Stack } from '@mui/material';

import { extractPlaceInfo } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Place } from '@barboxx/models';
import { DEFAULT_HOURS_OF_OPERATIONS } from '@barboxx/types';

import { RootContainer } from '../../components/nav/RootContainer';
import { PlaceForm } from '../components/PlaceForm';
import { CREATE_PLACE_SCHEMA } from '../components/PlaceForm/utils';
import { NewPlaceMap, NewPlaceMapRefProps } from './components/NewPlaceMap';

const INITIAL_VALUES: Place.CreateRequest = {
    name: '',
    streetAddress: '',
    zip: '',
    state: '',
    city: '',
    latitude: 0,
    longitude: 0,
    category: 'Bar',
    phoneNumber: '',
    website: '',
    mainImage: '',
    description: null,
    enabled: true,
    hoursOfOperations: DEFAULT_HOURS_OF_OPERATIONS,
};

export function PlaceCreate() {
    const notifications = useNotifications();
    const mapRef = useRef<NewPlaceMapRefProps | null>(null);
    const formContext = useForm<Place.CreateRequest>({
        resolver: yupResolver(CREATE_PLACE_SCHEMA),
        defaultValues: INITIAL_VALUES,
    });

    const mutatePlace = useMutation({
        mutationKey: ['createPlace'],
        mutationFn: (data: Place.CreateRequest) => Api.Places.createPlace(data),
    });

    const handleFormSave = (values: Place.CreateRequest) =>
        mutatePlace
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(
                    `Successfully created new place: "${data?.name}"`,
                    {
                        severity: 'success',
                    }
                );
                formContext.reset(INITIAL_VALUES);
                mapRef.current?.reset();
            })
            .catch(e => {
                notifications.show(e.message, { severity: 'error' });
            });

    const handleBusinessSelect = (data: google.maps.places.PlaceResult) => {
        const businessData = extractPlaceInfo(data);
        formContext.reset(businessData, { keepDefaultValues: true });
    };

    const activePage = useActivePage();

    const breadcrumbs = [
        ...(activePage?.breadcrumbs ?? []),
        { title: 'Create', path: `${activePage?.path}/create` },
    ];

    return (
        <RootContainer breadcrumbs={breadcrumbs} title="Create Place">
            <Stack gap={2} sx={{ paddingBottom: 4 }}>
                <Card>
                    <CardHeader title="Basic Info" />
                    <CardContent>
                        <PlaceForm
                            onSubmit={handleFormSave}
                            formContext={formContext}
                            editableOnLoad
                        />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader title="Find business" />
                    <CardContent>
                        <NewPlaceMap
                            onBusinessSelect={handleBusinessSelect}
                            ref={mapRef}
                        />
                    </CardContent>
                </Card>
            </Stack>
        </RootContainer>
    );
}
